import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { correoConDosExtensiones, ValidateEmail } from '@app/utils/validadors';
import { saveTenantFormDataBeforeInactivity } from '@app/utils/inactivity-helpers.util';
import { ensureLeadResumeDataInStore, ensureCriticalDataBeforeSubmit } from '@app/utils/lead-resume-sync-helpers.util';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalTerminosComponent } from '@app/refactoringComponents/modal-terminos/modal-terminos.component';
import { DatePipe } from '@angular/common';
import { selectDataByKey, selectDataByKeyOrDefault } from 'src/storage/general.selectors';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { setData } from 'src/storage/general.actions';
import { OrquestadorService } from '@app/services/orquestador/orquestador.service';
import { GeneralDataService } from '@app/services/general/general-data.service';
import { Router } from '@angular/router';
import { InactivityService } from '@app/services/inactivity/inactivity.service';
import { LoaderService } from '@app/services/general/loader.service';
import { LeadResumeSyncService } from '@app/services/lead-resume/lead-resume-store-sync.service';
import { LeadResumeService } from '@app/services/lead-resume/lead-resume.service';
import { DataLayerService } from '@app/services/dataLeyer/data-leyer.service';

/**
 * Componente para captura de datos básicos del inquilino
 * Implementa lógica de inactividad y restauración de datos
 */
@Component({
  selector: 'app-new-datos-inquilino',
  providers: [DatePipe],
  templateUrl: './new-datos-inquilino.component.html',
  styleUrls: ['./new-datos-inquilino.component.scss']
})
export class NewDatosInquilinoComponent implements OnInit, OnDestroy {
  // Servicios inyectados con inject()
  private readonly loaderService = inject(LoaderService);
  private readonly leadResumeSyncService = inject(LeadResumeSyncService);
  private readonly leadResumeService = inject(LeadResumeService);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(NgbModal);
  private readonly datePipe = inject(DatePipe);
  private readonly store = inject(Store);
  private readonly orquestadorService = inject(OrquestadorService);
  private readonly generalDataService = inject(GeneralDataService);
  inactivityService = inject(InactivityService);

  // Configuración de inactividad
  private readonly inactivityKeys: ('PolicyHolder' | 'Tenant' | 'Property' | 'ProductCode')[] =
    ['Tenant', 'Property', 'PolicyHolder', 'ProductCode'];

  // Tipos de documento disponibles
  readonly DocumentType = [
    { value: 'C', viewValue: 'Cédula de ciudadanía' }
  ];

  // Propiedades del componente
  myForm!: FormGroup;
  inquilino$: Observable<any>;
  asesorCode = '';
  bitacoraId = '';
  generatedBody: any;
  isClicked = false;
  isLoading = false;
  loaded = false;
  synced = false; // Flag para saber si se sincronizó Lead Resume
  sessionId = '';

  private subscriptions = new Subscription();

  constructor() {
    this.inquilino$ = this.store.select(selectDataByKey('Tenant'));

  }

  ngOnInit(): void {

    this.inactivityService.stopTimer();

    this.sessionId = this.getLocalStorage().toString();

    // Configurar loader
    this.setupLoader();



    // 🔹 Si se sincronizaron datos del Lead Resume, asegurar que estén en el store
    // if (this.synced) {
    //   ensureLeadResumeDataInStore(
    //     this.leadResumeService,
    //     this.leadResumeSyncService,
    //     this.store,
    //     'NewDatosInquilinoComponent'
    //   );

    //   console.log('✅ Datos de Lead Resume asegurados en el store');
    // }

    this.subscriptions.add(
      this.store.select(selectDataByKey('isLeadResume')).subscribe(isLeadResume => {
        if (isLeadResume) {

          console.log('🔄 Flag de Lead Resume detectado en store - Ocultando botón volver', isLeadResume);

          // 🔹 IMPORTANTE: Sincronizar datos del Lead Resume ANTES de inicializar formulario
          // Esto asegura que los datos estén en el store cuando las suscripciones se ejecuten
          console.log('🔄 Sincronizando Lead Resume en datos-inquilino...');
          // this.synced = this.leadResumeSyncService.syncAllLeadResumeData();
          console.log('📊 Lead Resume sincronizado:', this.synced ? 'Sí' : 'No');
        }
      })
    );

     // Suscribirse al código de asesor
    this.subscriptions.add(
      this.store.select(selectDataByKey('codigoAsesor')).subscribe(code => {
        if (code) {
          console.log('Código de asesor desde el store:', code);
          this.asesorCode = code;
        }
      })
    );

    // Inicializar formulario (ahora los datos del Lead Resume ya están en el store)
    this.initializeForm();

    // Iniciar timer de inactividad
    this.startInactivityTimer();

    // Ocultar loader después de cargar datos
    setTimeout(() => this.loaderService.hide(), 500);

    // Log de DataLayer
    this.dataLayerService.logDataNewFlow(
      'Arriendo_ecommerce_step_4_Datos_Inquilino',
      'Arriendo',
      'Datos_Inquilino',
      'Pageview',
      this.sessionId
    );

  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.inactivityService.stopTimer();
  }

  /**
   * Configura el loader del componente
   */
  private setupLoader(): void {
    this.loaderService.show();
    this.subscriptions.add(
      this.loaderService.loading$.subscribe(({ loading, loaded }) => {
        this.isLoading = loading;
        this.loaded = loaded;
      })
    );
  }

  /**
   * Inicializa el formulario y carga datos previos si existen
   */
  private initializeForm(): void {


    // Crear formulario
    this.myForm = this.fb.group({
      FirstName: ['', [Validators.required, Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/), this.nameValidator()]],
      DocumentType: ['C', Validators.required],
      DocumentNumber: ['', [Validators.required, Validators.minLength(7), Validators.maxLength(10), ]],
      Mail: ['', [Validators.required, ValidateEmail()]]
    });


    // Cargar datos guardados (prioridad: TenantFormData > Tenant)
    this.loadSavedFormData();
  }

  /**
   * Carga datos guardados en el store (de inactividad o submit previo)
   */
  private loadSavedFormData(): void {
    // Primero intentar restaurar desde TenantFormData (datos guardados durante inactividad)
    this.subscriptions.add(
      this.store.select(selectDataByKey('TenantFormData')).subscribe(formData => {
        if (formData && formData.FirstName !== '' && formData.Mail !== '') {
          console.log('✅ Restaurando datos de formulario de inquilino desde TenantFormData:', formData);
          console.log('📋 Valores a cargar:', {
            FirstName: formData.FirstName,
            DocumentType: formData.DocumentType,
            DocumentNumber: formData.DocumentNumber,
            Mail: formData.Mail
          });
          this.myForm.patchValue({
            FirstName: formData.FirstName || '',
            DocumentType: formData.DocumentType || 'C',
            DocumentNumber: formData.DocumentNumber || '',
            Mail: formData.Mail || ''
          });
          console.log('✅ Formulario actualizado:', this.myForm.value);
        } else {
          console.log('ℹ️ No hay TenantFormData, intentando cargar desde Tenant');
          // Si no hay TenantFormData, cargar desde Tenant (datos del submit exitoso o Lead Resume)
          this.loadFromTenant();
        }
      })
    );
  }

  /**
   * Carga datos desde Tenant (submit exitoso previo o Lead Resume)
   */
  private loadFromTenant(): void {
    this.subscriptions.add(
      this.inquilino$.subscribe(tenant => {
        if (tenant) {
          console.log('✅ Cargando datos desde Tenant del store:', tenant);
          console.log('📋 Valores disponibles:', {
            FirstName: tenant.FirstName,
            DocumentType: tenant.DocumentType,
            DocumentNumber: tenant.DocumentNumber,
            Mail: tenant.Mail
          });
          this.myForm.patchValue({
            FirstName: tenant.FirstName || '',
            DocumentType: tenant.DocumentType || 'C',
            DocumentNumber: tenant.DocumentNumber || '',
            Mail: tenant.Mail || ''
          });
          console.log('✅ Formulario actualizado desde Tenant:', this.myForm.value);
        } else {
          console.log('ℹ️ No hay datos de Tenant en el store (usuario nuevo o Lead Resume sin Tenant)');
        }
      })
    );
  }

  /**
   * Inicia el timer de inactividad
   */
  private startInactivityTimer(): void {
    this.inactivityService.startInactivityTimer(
      'seguros/hogar/arriendo/cotizador/inactividad',
      undefined,
      this.inactivityKeys,
      '20',
      'Abandono Datos básicos inquilino',
      undefined,
      false,
      () => this.saveFormDataBeforeInactivity(),
      'Datos-basicos-inquilino'
    );
  }

  /**
   * Guarda los datos del formulario antes de redirigir por inactividad
   */
  private saveFormDataBeforeInactivity(): void {
    saveTenantFormDataBeforeInactivity(this.myForm.value, this.store, this.datePipe);
  }

  /**
   * Validador personalizado para el nombre (solo letras, sin espacios)
   */
  private nameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';
      const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñ]+$/;
      return regex.test(value) ? null : { invalidName: true };
    };
  }

  /**
   * Abre el modal de términos y condiciones
   */
  openModalTerminos(): void {
    this.modalService.open(ModalTerminosComponent);
  }

  /**
   * Mensajes de error para el campo de número de documento
   */
  getDocumentNumberErrorMessage(): string {
    const control = this.myForm.get('DocumentNumber');

    if (control?.hasError('required')) {
      return 'Este campo debe tener entre 7 y 10 dígitos';
    }

    if (control?.hasError('minlength') || control?.hasError('maxlength')) {
      return 'Este campo debe tener entre 7 y 10 dígitos';
    }

    return 'Parece que dejaste un campo obligatorio vacío';
  }

  /**
   * Mensajes de error para el campo de nombre
   */
  getNameErrorMessage(): string {
    const control = this.myForm.get('FirstName');

    if (control?.hasError('required')) {
      return 'Asegúrate de llenar este campo';
    }

    if (control?.hasError('invalidName')) {
      return 'Ingresa solo el primer nombre de tu inquilino, sin apellidos';
    }

    return 'Asegúrate de llenar este campo';
  }

  /**
   * Submit del formulario - Guarda datos y llama al orquestador
   */
  submit(): void {
    this.isClicked = true;

    this.dataLayerService.logDataNewFlow(
      'Arriendo_ecommerce_step_4_Datos_Inquilino',
      'Arriendo',
      'Datos_Inquilino',
      'Button',
      this.sessionId
    );

    if (!this.myForm.valid) {
      return;
    }

    // Guardar datos en el store
    this.saveTenantData();

    // Preparar y enviar al orquestador
    this.sendToOrquestador();
  }

  /**
   * Guarda los datos del Tenant en el store
   */
  private saveTenantData(): void {
    const formattedDate = this.datePipe.transform(new Date(), 'dd/MM/yyyy') || '';
    const formValues = this.myForm.value;

    const Tenant = {
      ...formValues,
      PersonalDataVersion: formattedDate,
      PersonalDataLastUpdated: formattedDate,
      PersonalDataAuthorization: true
    };

    console.log('💾 Guardando Tenant completo con datos del formulario:', Tenant);

    this.store.dispatch(setData({
      key: 'Tenant',
      value: Tenant,
      merge: true // ✅ NO hacer merge, reemplazar completamente con datos del formulario
    }));

    this.store.dispatch(setData({
      key: 'TenantFormData',
      value: Tenant,
      merge: true,
    }));
  }

  /**
   * Envía los datos al orquestador y navega según la respuesta
   */
  private sendToOrquestador(): void {
    // 🔹 Asegurar que todos los datos críticos estén en el store antes de generar el body
    // ⚠️ IMPORTANTE: Excluir 'Tenant' y 'Property' porque:
    // - Tenant: el usuario acaba de llenar el formulario (FirstName, Mail)
    // - Property: el usuario llenó estos datos en la página de inmueble (ContractStartDate, PropertyAddress, etc.)
    // NO queremos sobrescribir con datos viejos del Lead Resume que no tienen estos campos
    // ensureCriticalDataBeforeSubmit(
    //   this.leadResumeService,
    //   this.leadResumeSyncService,
    //   this.store,
    //   'NewDatosInquilinoComponent',
    //   true, // Mantener en true para consistencia
    //   ['Tenant', 'Property'] // ✅ Excluir ambos para NO sobrescribir datos de formularios previos
    // );

    window.scrollTo(0, 0);
    this.loaderService.show();

    // Pequeño delay para asegurar que el store se actualice antes de getbody()
    setTimeout(() => {
      const options = {
        keys: ['PolicyHolder', 'Tenant', 'Property', 'ProductCode', 'optionalProducts'],
        leadId: 'leadId',
        screenPercentage: '40',
        asesorCode: this.asesorCode || undefined,
        bitacoraId: this.bitacoraId || undefined
      };

      this.generalDataService.getbody(options).subscribe(body => {
        this.generatedBody = body;
        this.generatedBody.optionalProducts = this.generatedBody.productsConfigurationData.optionalProducts
        console.log('Generated body (datos-inquilino):', this.generatedBody);
        console.log('📋 leadId en body:', this.generatedBody.leadId ? `✅ ${this.generatedBody.leadId}` : '⚠️ No disponible');

        // Agregar propiedades adicionales al body
        this.generatedBody.createQuote = true;
        this.generatedBody.leadRequirement = 'Abandono Pendiente firma inquilino';
        this.generatedBody.estadoRetoma = 'Habilitado';
        this.generatedBody.estadoRescate = 'Inhabilitado';
        this.generatedBody.pantalla = 'Firma-pendiente-inquilno';

        console.log('Generated body:', this.generatedBody);

        // Llamar al orquestador
        this.orquestadorService.postOrquestador(this.generatedBody).subscribe({
          next: (response) => this.handleOrquestadorResponse(response)
        });
      });
    }, 0); // Fin del setTimeout - permite que el store se actualice
  }

  /**
   * Maneja la respuesta del orquestador
   */
  private handleOrquestadorResponse(response: any): void {

    
    if (!response.URLRetomaInquilino) {
      this.router.navigate(['seguros/hogar/arriendo/cotizador/problemas-tecnicos']);
      this.loaderService.hide()
      return;
    }

    // Guardar URL de retoma en el store
    this.store.dispatch(setData({
      key: 'URLRetomaInquilino',
      value: response.URLRetomaInquilino,
      merge: false
    }));

    // Navegar a compartir link
    this.loaderService.hide();
    this.inactivityService.stopTimer();
    this.router.navigate(['seguros/hogar/arriendo/cotizador/compartir-link-inquilino']);
  }

  /**
   * Obtiene el session_id del localStorage
   */
  private getLocalStorage(): number {
    return Number(localStorage.getItem('session_id'));
  }
}
